import ContactService from "@/services/contactService";

export const ContactStore = {
    namespaced: true,
    state: {
        tabs: {
            all: {
                contact_id: null,
                name: "",
                tableData: [],
                totalRecords: 0,
                rowsPerPage: 20,
                currentPage: 1,
                sortField: "name",
                sortOrder: "1",
                first: 0,
                is_customer: undefined,
                is_supplier: undefined,
                is_agent: undefined,
                is_individual: undefined,
                searchActions: false,
            },
            search: {
                contact_id: null,
                name: "",
                searchTerm: "",
                tableData: [],
                totalRecords: 0,
                rowsPerPage: 20,
                currentPage: 1,
                sortField: "name",
                sortOrder: "1",
                first: 0,
                is_customer: undefined,
                is_supplier: undefined,
                is_agent: undefined,
                is_individual: undefined,
                searchActions: false,
            },
        },

        currentTab: "all",
        searchText: "",
        isLoading: false,
    },

    mutations: {
        SET_TABLE_DATA(state, payload) {
            const tab = payload.tab;
            const data = payload.data;

            const rows = data && data.data ? data.data : [];
            const count = data && typeof data.count === "number" ? data.count : 0;

            state.tabs[tab].tableData = rows;
            state.tabs[tab].totalRecords = count;
        },

        UPDATE_CURRENT_PAGE(state, { tab, page }) {
            state.tabs[tab].currentPage = page;
            const rowsPerPage = state.tabs[tab].rowsPerPage;
            state.tabs[tab].first = page > 0 ? (page - 1) * rowsPerPage : 0;
        },

        UPDATE_ROWS_PER_PAGE(state, { tab, rows }) {
            state.tabs[tab].rowsPerPage = rows;
        },

        UPDATE_SORT_OPTIONS(state, { tab, field, order }) {
            state.tabs[tab].sortField = field;
            state.tabs[tab].sortOrder = order;
        },

        RESET_FIRST_VALUE(state, { tab }) {
            const currentPage = state.tabs[tab].currentPage;
            const rowsPerPage = state.tabs[tab].rowsPerPage;
            state.tabs[tab].first = currentPage > 0 ? (currentPage - 1) * rowsPerPage : 0;
        },

        RESET_CURRENT_PAGE(state, { tab, page = 1 }) {
            state.tabs[tab].currentPage = page;
            state.tabs[tab].first = 0;
        },

        UPDATE_SEARCH_TEXT(state, searchText) {
            state.searchText = searchText;
        },

        UPDATE_CURRENT_TAB(state, currentTab) {
            state.currentTab = currentTab;
        },

        SET_LOADING(state, isLoading) {
            state.isLoading = isLoading;
        },

        UPDATE_TAB_FILTER(state, { tab, key, value }) {
            state.tabs[tab][key] = value;
        },

        RESET_TAB_FILTERS(state, { tab }) {
            state.tabs[tab].contact_id = null;
            state.tabs[tab].name = "";
            state.tabs[tab].is_customer = undefined;
            state.tabs[tab].is_supplier = undefined;
            state.tabs[tab].is_agent = undefined;
            state.tabs[tab].is_individual = undefined;
            state.tabs[tab].searchActions = false;

            if (tab === "search") {
                state.tabs[tab].searchTerm = "";
            }

            state.tabs[tab].currentPage = 1;
            state.tabs[tab].first = 0;
        },
    },

    actions: {
        async fetchTableData({ commit, state, dispatch }) {
            commit("SET_LOADING", true);

            const tab = state.currentTab;
            const currentTabState = state.tabs[tab];

            const params = buildParams(currentTabState, state);
            const queryString = buildQueryString(params);

            try {
                const data = await ContactService.searchContacts(`?${queryString}`);
                commit("SET_TABLE_DATA", { tab, data });
            } catch (error) {
                commit("SET_TABLE_DATA", { tab, data: { data: [], count: 0 } });
                console.error("Error fetching data:", error);
            } finally {
                commit("SET_LOADING", false);
                dispatch("resetFirstValue");
            }
        },

        updateTableSettings({ commit, dispatch, state }, { rows, page }) {
            const tab = state.currentTab;

            const prevRows = Number(state.tabs[tab].rowsPerPage);
            const nextRows = Number(rows);
            const incomingPage0 = Number(page);

            commit("UPDATE_ROWS_PER_PAGE", { tab, rows: nextRows });

            if (nextRows !== prevRows) {
                commit("RESET_CURRENT_PAGE", { tab, page: 1 });
            } else {
                commit("UPDATE_CURRENT_PAGE", { tab, page: incomingPage0 + 1 });
            }

            dispatch("fetchTableData");
        },

        updateSortOptions({ commit, dispatch, state }, { field, order }) {
            commit("UPDATE_SORT_OPTIONS", { tab: state.currentTab, field, order });
            dispatch("resetCurrentPage", 1);
            dispatch("fetchTableData");
        },

        updateSearchCriteria({ commit, dispatch }, { searchText }) {
            commit("UPDATE_SEARCH_TEXT", searchText);
            dispatch("resetCurrentPage", 1);
            dispatch("fetchTableData");
        },

        updateTab({ commit, dispatch }, currentTab) {
            commit("UPDATE_CURRENT_TAB", currentTab);
            dispatch("resetCurrentPage", 1);
            dispatch("fetchTableData");
        },

        updateTabFilter({ commit, dispatch, state }, { key, value }) {
            commit("UPDATE_TAB_FILTER", { tab: state.currentTab, key, value });
            dispatch("resetCurrentPage", 1);
            dispatch("fetchTableData");
        },

        resetTabFilters({ commit, dispatch, state }) {
            commit("RESET_TAB_FILTERS", { tab: state.currentTab });
            dispatch("fetchTableData");
        },

        resetFirstValue({ commit, state }) {
            commit("RESET_FIRST_VALUE", { tab: state.currentTab });
        },

        resetCurrentPage({ commit, state }, page = 1) {
            commit("RESET_CURRENT_PAGE", { tab: state.currentTab, page });
        },
    },
};

function buildQueryString(params) {
    return Object.entries(params)
        .filter((entry) => {
            const value = entry[1];
            return value !== undefined && value !== null && value !== "";
        })
        .map((entry) => {
            const key = entry[0];
            const value = entry[1];
            return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
        })
        .join("&");
}

function buildParams(currentTabState, state) {
    const params = {
        page: currentTabState.currentPage,
        limit: currentTabState.rowsPerPage,
        orderBy: currentTabState.sortField,
        order: currentTabState.sortOrder,
        tab: state.currentTab,
    };

    if (currentTabState.contact_id) params.contact_id = currentTabState.contact_id;
    if (currentTabState.name) params.name = currentTabState.name;

    if (currentTabState.searchActions === true) params.searchActions = true;

    if (currentTabState.is_customer !== undefined) params.is_customer = currentTabState.is_customer;
    if (currentTabState.is_supplier !== undefined) params.is_supplier = currentTabState.is_supplier;
    if (currentTabState.is_agent !== undefined) params.is_agent = currentTabState.is_agent;
    if (currentTabState.is_individual !== undefined) params.is_individual = currentTabState.is_individual;

    if (state.currentTab === "search" && state.searchText) {
        params.searchTerm = state.searchText;
    }

    return params;
}
